import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Radio,
  RadioGroup,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure
} from '@chakra-ui/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { EMPTY_CELL_VALUE, EmptyCell } from '../../../components/EmptyCell'
import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { Icon } from '../../../components/Icon'
import { LoadingBlock } from '../../../components/Loading'
import { MoneyInput } from '../../../components/MaskedInput'
import { SectionTitle } from '../../../components/SectionCard'
import { SearchableSelect } from '../../../components/Select'
import { StatusBadge, StatusMap } from '../../../components/StatusBadge'
import { Tooltip } from '../../../components/Tooltip'
import { formatCurrencyValue, formatLocalDateTime, toDateTimeLocalValue, useAppToast } from '../../../helpers'
import { CoursesOnlineService } from '../../../services/CoursesOnline/CoursesOnlineService'
import { CoursesService } from '../../../services/CoursesService/CoursesService'
import { IBulkPriceSchedule, TBulkModality, TBulkMode } from '../../../services/CoursesService/interfaces'
import { BULK_MODALITY } from './bulkPriceConstants'
import { buildBulkPayload, isValidBulkForm } from './bulkPricePayload'
import EditBulkScheduleModal from './EditBulkScheduleModal'

type TModality = TBulkModality
type TMode = TBulkMode

interface ISelectedEntry {
  course_id: number
  course_name: string
  price_type: TModality
}

interface IBulkEditPricesModalProps {
  isOpen: boolean
  onClose: () => void
}

/** Status do agendamento de preço — só existe aqui, mas na forma que o `<StatusBadge>` lê. */
const PRICE_SCHEDULE_STATUS: StatusMap = {
  pending: { label: 'Agendado', colorScheme: 'yellow' },
  executed: { label: 'Executado', colorScheme: 'green' },
  cancelled: { label: 'Cancelado', colorScheme: 'red' }
}

const MODALITIES: TModality[] = ['in_person', 'live_online', 'recorded']

const formatScheduledAt = (value: string): string => formatLocalDateTime(value) || EMPTY_CELL_VALUE

export default function BulkEditPricesModal({ isOpen, onClose }: IBulkEditPricesModalProps) {
  const coursesService = new CoursesService()
  const recordedService = new CoursesOnlineService()
  const toast = useAppToast()

  // Track which modality request is currently pending (to avoid race conditions)
  const pendingModalityRef = useRef<TModality | null>(null)

  // Step control
  const [step, setStep] = useState<'modality' | 'edit'>('modality')
  const [selectedModality, setSelectedModality] = useState<TModality | null>(null)

  // Course list (filtered by selected modality)
  const [courses, setCourses] = useState<{ id: number; name: string }[]>([])
  const [loadingCourses, setLoadingCourses] = useState(false)

  // Selected course ids
  const [selectedCourseIds, setSelectedCourseIds] = useState<number[]>([])

  // Edit mode
  const [mode, setMode] = useState<TMode>('discount')

  // Price fields
  const [discount, setDiscount] = useState('')
  const [taxaMatricula, setTaxaMatricula] = useState<number | null>(null)
  const [priceOriginal, setPriceOriginal] = useState<number | null>(null)

  // Coupon fields
  const [removeCoupon, setRemoveCoupon] = useState(false)
  const [couponValue, setCouponValue] = useState('')

  // CTA text field
  const [ctaText, setCtaText] = useState('')

  // Schedule fields
  const [isScheduled, setIsScheduled] = useState(false)
  const [scheduledAt, setScheduledAt] = useState('')

  const [saving, setSaving] = useState(false)

  // Scheduled changes history
  const [schedules, setSchedules] = useState<IBulkPriceSchedule[]>([])
  const [schedulesLoading, setSchedulesLoading] = useState(false)
  const [schedulesPage, setSchedulesPage] = useState(1)
  const [schedulesLastPage, setSchedulesLastPage] = useState(1)
  const [schedulesTotal, setSchedulesTotal] = useState(0)
  const [schedulesFilter, setSchedulesFilter] = useState<TModality | ''>('')

  // Edit schedule modal
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  const [editingSchedule, setEditingSchedule] = useState<IBulkPriceSchedule | null>(null)

  const loadSchedules = useCallback(
    (page = 1, filter: TModality | '' = schedulesFilter) => {
      setSchedulesLoading(true)
      coursesService
        .listBulkPriceSchedules({
          ...(filter ? { modality: filter as TModality } : {}),
          page,
          per_page: 5
        })
        .then((res) => {
          setSchedules(res.data)
          setSchedulesPage(res.current_page)
          setSchedulesLastPage(res.last_page)
          setSchedulesTotal(res.total)
        })
        .catch(() => {
          // Non-critical, silently fail
        })
        .finally(() => setSchedulesLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schedulesFilter]
  )

  const resetEditState = () => {
    setSelectedCourseIds([])
    setMode('discount')
    setDiscount('')
    setTaxaMatricula(null)
    setPriceOriginal(null)
    setRemoveCoupon(false)
    setCouponValue('')
    setCtaText('')
    setIsScheduled(false)
    setScheduledAt('')
  }

  // Reset everything when modal opens
  useEffect(() => {
    if (!isOpen) return
    pendingModalityRef.current = null
    setStep('modality')
    setSelectedModality(null)
    setCourses([])
    resetEditState()
    setSchedulesPage(1)
    setSchedulesFilter('')
    loadSchedules(1, '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Load courses for the selected modality
  const handleModalitySelect = (modality: TModality) => {
    pendingModalityRef.current = modality
    setSelectedModality(modality)
    setStep('edit')
    resetEditState()
    setCourses([])

    setLoadingCourses(true)

    const load =
      modality === 'recorded'
        ? recordedService.listForSelect()
        : coursesService.listForSelect(
            modality === 'in_person' ? true : undefined,
            modality === 'live_online' ? true : undefined,
            undefined
          )

    load
      .then((data) => {
        // Only update if this request is still the current one
        if (pendingModalityRef.current === modality) {
          setCourses(data)
        }
      })
      .catch(() => {
        // Only show error if this request is still current
        if (pendingModalityRef.current === modality) {
          toast.error('Erro ao carregar cursos')
        }
      })
      .finally(() => {
        // Only clear loading if this request is still current
        if (pendingModalityRef.current === modality) {
          setLoadingCourses(false)
        }
      })
  }

  const handleBack = () => {
    pendingModalityRef.current = null
    setStep('modality')
    setSelectedModality(null)
    setCourses([])
    resetEditState()
    loadSchedules(1, schedulesFilter)
  }

  const handleFilterChange = (filter: TModality | '') => {
    setSchedulesFilter(filter)
    loadSchedules(1, filter)
  }

  const handleCancelSchedule = async (id: number) => {
    try {
      await coursesService.deleteBulkPriceSchedule(id)
      toast.info('Agendamento cancelado.')
      loadSchedules(schedulesPage, schedulesFilter)
    } catch {
      toast.error('Erro ao cancelar agendamento.')
    }
  }

  const handleEditSchedule = (schedule: IBulkPriceSchedule) => {
    setEditingSchedule(schedule)
    onEditOpen()
  }

  // ReactSelect options/value
  const selectOptions = courses.map((c) => ({ value: c.id, label: c.name }))

  const handleAddAll = () => {
    setSelectedCourseIds(courses.map((c) => c.id))
  }

  const selectedCourses = courses.filter((c) => selectedCourseIds.includes(c.id))

  // Entries built from selectedCourseIds + selectedModality (for the prices API)
  const entries: ISelectedEntry[] = selectedCourses.map((c) => ({
    course_id: c.id,
    course_name: c.name,
    price_type: selectedModality!
  }))

  // Price preview
  const previewPrice =
    priceOriginal != null && discount ? priceOriginal * (1 - parseFloat(discount) / 100) : priceOriginal

  // Validation
  const isValid = isValidBulkForm({
    mode,
    selectedCourseIds,
    discount,
    taxaMatricula,
    priceOriginal,
    removeCoupon,
    couponValue,
    isScheduled,
    scheduledAt
  })

  const handleSave = async () => {
    if (!isValid || !selectedModality) return
    setSaving(true)
    try {
      if (isScheduled) {
        const payload = buildBulkPayload({
          mode,
          selectedCourseIds,
          modality: selectedModality,
          discount,
          taxaMatricula,
          priceOriginal,
          removeCoupon,
          couponValue,
          ctaText,
          entries: entries.map((e) => ({ course_id: e.course_id, price_type: e.price_type }))
        })
        await coursesService.createBulkPriceSchedule({
          modality: selectedModality,
          mode,
          scheduled_at: scheduledAt,
          payload
        })
        toast.success('Alteração agendada!', `Será aplicada em ${formatScheduledAt(scheduledAt)}.`)
        handleBack()
      } else if (mode === 'coupon') {
        const result = await recordedService.bulkUpdateCoupons(
          buildBulkPayload({
            mode,
            selectedCourseIds,
            modality: selectedModality,
            discount,
            taxaMatricula,
            priceOriginal,
            removeCoupon,
            couponValue,
            ctaText
          }) as { course_ids: number[]; coupon: string | null; cta_text?: string }
        )
        toast.success('Cupons atualizados', result.message)
        onClose()
      } else {
        const payload = buildBulkPayload({
          mode,
          selectedCourseIds,
          modality: selectedModality,
          discount,
          taxaMatricula,
          priceOriginal,
          removeCoupon,
          couponValue,
          ctaText,
          entries: entries.map((e) => ({ course_id: e.course_id, price_type: e.price_type }))
        })
        const result = await coursesService.bulkUpdatePrices(
          payload as Parameters<typeof coursesService.bulkUpdatePrices>[0]
        )
        toast.success('Preços atualizados', result.message)
        onClose()
      }
    } catch {
      toast.error('Erro ao salvar', 'Não foi possível salvar as alterações.')
    } finally {
      setSaving(false)
    }
  }

  const saveLabel = (() => {
    const count = selectedCourseIds.length
    const action = isScheduled ? 'Agendar' : 'Salvar'
    if (count === 0) return action
    return `${action} ${count} curso${count !== 1 ? 's' : ''}`
  })()

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title="Editar Preços em Lote"
        size="wide"
        footer={
          <>
            <Button variant="ghost" onClick={onClose} isDisabled={saving}>
              Cancelar
            </Button>
            {step === 'edit' && (
              <Button
                colorScheme={isScheduled ? 'orange' : 'magenta'}
                onClick={handleSave}
                isLoading={saving}
                isDisabled={!isValid}
                loadingText={isScheduled ? 'Agendando…' : 'Salvando…'}
              >
                {saveLabel}
              </Button>
            )}
          </>
        }
      >
        {step === 'modality' ? (
          <>
            {/* ── Step 1: Choose modality ── */}
            <SectionTitle>Escolha a modalidade</SectionTitle>
            <SimpleGrid columns={3} spacing={3}>
              {MODALITIES.map((modality) => (
                <Button
                  key={modality}
                  variant="outline"
                  colorScheme={BULK_MODALITY[modality].colorScheme as 'orange' | 'green' | 'blue'}
                  onClick={() => handleModalitySelect(modality)}
                  height="72px"
                  fontSize="sm"
                  fontWeight="semibold"
                >
                  {BULK_MODALITY[modality].label}
                </Button>
              ))}
            </SimpleGrid>

            {/* ── Scheduled changes history ── */}
            <Divider my={6} />

            <SectionTitle
              mb={3}
              actions={
                <ButtonGroup size="xs" isAttached variant="outline">
                  <Button
                    colorScheme={schedulesFilter === '' ? 'magenta' : 'gray'}
                    variant={schedulesFilter === '' ? 'solid' : 'outline'}
                    onClick={() => handleFilterChange('')}
                  >
                    Todas
                  </Button>
                  {MODALITIES.map((m) => (
                    <Button
                      key={m}
                      colorScheme={
                        schedulesFilter === m ? (BULK_MODALITY[m].colorScheme as 'orange' | 'green' | 'blue') : 'gray'
                      }
                      variant={schedulesFilter === m ? 'solid' : 'outline'}
                      onClick={() => handleFilterChange(m)}
                    >
                      {BULK_MODALITY[m].label}
                    </Button>
                  ))}
                </ButtonGroup>
              }
            >
              Alterações agendadas
              {schedulesTotal > 0 && (
                <Badge ml={2} colorScheme="gray">
                  {schedulesTotal}
                </Badge>
              )}
            </SectionTitle>

            {schedulesLoading ? (
              <LoadingBlock />
            ) : schedules.length === 0 ? (
              <Box
                borderWidth="1px"
                borderRadius="md"
                borderStyle="dashed"
                borderColor="gray.300"
                py={6}
                textAlign="center"
              >
                <Text variant="caption">Nenhuma alteração agendada</Text>
              </Box>
            ) : (
              <TableContainer borderWidth="1px" borderRadius="md">
                <Table>
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Modalidade</Th>
                      <Th>Data agendada</Th>
                      <Th>Criado por</Th>
                      <Th>Status</Th>
                      <Th w="80px" />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {schedules.map((s) => (
                      <Tr key={s.id}>
                        <Td>
                          <StatusBadge status={s.modality} statuses={BULK_MODALITY} />
                        </Td>
                        <Td whiteSpace="nowrap">{formatScheduledAt(s.scheduled_at)}</Td>
                        <Td>
                          <EmptyCell value={s.created_by_name} />
                        </Td>
                        <Td>
                          <StatusBadge status={s.status} statuses={PRICE_SCHEDULE_STATUS} />
                        </Td>
                        <Td>
                          {s.status === 'pending' && (
                            <Flex gap={1}>
                              <Tooltip label="Editar">
                                <IconButton
                                  aria-label="Editar agendamento"
                                  icon={<Icon name="lni lni-pencil" />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="blue"
                                  onClick={() => handleEditSchedule(s)}
                                />
                              </Tooltip>
                              <Tooltip label="Cancelar">
                                <IconButton
                                  aria-label="Cancelar agendamento"
                                  icon={<Icon name="lni lni-close" />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="red"
                                  onClick={() => handleCancelSchedule(s.id)}
                                />
                              </Tooltip>
                            </Flex>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}

            {/* Pagination */}
            {schedulesLastPage > 1 && (
              <Flex align="center" justify="flex-end" mt={3} gap={2}>
                <Button
                  size="xs"
                  variant="ghost"
                  isDisabled={schedulesPage === 1}
                  onClick={() => {
                    const p = schedulesPage - 1
                    setSchedulesPage(p)
                    loadSchedules(p, schedulesFilter)
                  }}
                >
                  ‹ Anterior
                </Button>
                <Text variant="caption">
                  {schedulesPage} / {schedulesLastPage}
                </Text>
                <Button
                  size="xs"
                  variant="ghost"
                  isDisabled={schedulesPage === schedulesLastPage}
                  onClick={() => {
                    const p = schedulesPage + 1
                    setSchedulesPage(p)
                    loadSchedules(p, schedulesFilter)
                  }}
                >
                  Próximo ›
                </Button>
              </Flex>
            )}
          </>
        ) : (
          <>
            {/* ── Step 2: Select courses + configure ── */}
            <Flex align="center" gap={3} mb={5}>
              <Button size="xs" variant="ghost" onClick={handleBack} leftIcon={<Icon name="lni lni-arrow-left" />}>
                Voltar
              </Button>
              <StatusBadge status={selectedModality!} statuses={BULK_MODALITY} py={1} />
            </Flex>

            {/* ── Schedule toggle ── */}
            <Box bg="gray.50" borderRadius="md" p={4} mb={5}>
              <FormControl w="auto" display="flex" alignItems="center" gap={3}>
                <Switch
                  id="schedule-toggle"
                  isChecked={isScheduled}
                  onChange={(e) => {
                    setIsScheduled(e.target.checked)
                    if (!e.target.checked) setScheduledAt('')
                  }}
                />
                <FormLabel htmlFor="schedule-toggle" mb={0} cursor="pointer" fontWeight="medium" fontSize="sm">
                  Agendar alteração
                </FormLabel>
              </FormControl>
              {isScheduled && (
                <FormField label="Data e hora da alteração" mt={3} maxW="280px">
                  <Input
                    type="datetime-local"
                    value={scheduledAt}
                    min={toDateTimeLocalValue()}
                    onChange={(e) => setScheduledAt(e.target.value)}
                  />
                </FormField>
              )}
            </Box>

            {/* ── Section 1: Course Selection ── */}
            <SectionTitle mb={3}>1. Selecionar Cursos</SectionTitle>

            <FormControl mb={4}>
              <Flex justify="space-between" align="center" mb={2}>
                <FormLabel fontSize="sm" m={0}>
                  Buscar cursos
                </FormLabel>
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="magenta"
                  onClick={handleAddAll}
                  isDisabled={loadingCourses || courses.length === 0}
                >
                  <Icon name="lni lni-checkmark" mr="4px" />
                  Adicionar Todos
                </Button>
              </Flex>
              {loadingCourses ? (
                <LoadingBlock entity="cursos" py={2} />
              ) : (
                <SearchableSelect<number>
                  isMulti
                  placeholder="Digite para buscar…"
                  entity="curso"
                  options={selectOptions}
                  value={selectedCourseIds}
                  onChange={setSelectedCourseIds}
                />
              )}
            </FormControl>

            {/* Selected courses table */}
            {selectedCourses.length > 0 ? (
              <TableContainer borderWidth="1px" borderRadius="md" mb={2}>
                <Table>
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Curso</Th>
                      <Th w="40px" />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {selectedCourses.map((course) => (
                      <Tr key={course.id}>
                        <Td fontWeight="medium">{course.name}</Td>
                        <Td>
                          <Tooltip label="Remover curso">
                            <IconButton
                              aria-label="Remover"
                              icon={<Icon name="lni lni-close" />}
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => setSelectedCourseIds((prev) => prev.filter((id) => id !== course.id))}
                            />
                          </Tooltip>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            ) : (
              <Box
                borderWidth="1px"
                borderRadius="md"
                borderStyle="dashed"
                borderColor="gray.300"
                py={6}
                textAlign="center"
                mb={2}
              >
                <Text variant="caption">Nenhum curso selecionado</Text>
              </Box>
            )}

            {selectedCourseIds.length > 0 && (
              <Text variant="caption" mb={4}>
                {selectedCourseIds.length} curso{selectedCourseIds.length !== 1 ? 's' : ''} selecionado
                {selectedCourseIds.length !== 1 ? 's' : ''}
              </Text>
            )}

            <Divider my={5} />

            {/* ── Section 2: What to change ── */}
            <SectionTitle>2. O que alterar</SectionTitle>

            <RadioGroup value={mode} onChange={(v) => setMode(v as TMode)} mb={5}>
              <Stack direction={{ base: 'column', md: 'row' }} spacing={6}>
                <Radio value="discount">
                  <Box>
                    <Text fontWeight="medium" fontSize="sm">
                      Aplicar Desconto (%)
                    </Text>
                    <Text variant="caption">Mantém o preço original, aplica um novo desconto</Text>
                  </Box>
                </Radio>
                <Radio value="prices">
                  <Box>
                    <Text fontWeight="medium" fontSize="sm">
                      Alterar Preços
                    </Text>
                    <Text variant="caption">Edita taxa de matrícula, preço e/ou desconto</Text>
                  </Box>
                </Radio>
                {selectedModality === 'recorded' && (
                  <Radio value="coupon">
                    <Box>
                      <Text fontWeight="medium" fontSize="sm">
                        Alterar Cupom
                      </Text>
                      <Text variant="caption">Adiciona ou remove o cupom da URL da Eduzz</Text>
                    </Box>
                  </Radio>
                )}
              </Stack>
            </RadioGroup>

            {mode === 'discount' && (
              <Box bg="gray.50" borderRadius="md" p={4}>
                <FormField label="Desconto (%)" maxW="200px">
                  <NumberInput min={0} max={100} value={discount} onChange={(val) => setDiscount(val)} precision={2}>
                    <NumberInputField placeholder="Ex: 15" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormField>
                {discount !== '' && (
                  <Text variant="caption" mt={2}>
                    O preço original de cada curso será mantido e receberá {discount}% de desconto.
                  </Text>
                )}
              </Box>
            )}

            {mode === 'prices' && (
              <Box bg="gray.50" borderRadius="md" p={4}>
                <Stack spacing={4} direction={{ base: 'column', md: 'row' }}>
                  <FormField label="Taxa de Matrícula">
                    <MoneyInput value={taxaMatricula} onChange={setTaxaMatricula} placeholder="Opcional" />
                  </FormField>
                  <FormField label="Preço Original">
                    <MoneyInput value={priceOriginal} onChange={setPriceOriginal} placeholder="Opcional" />
                  </FormField>
                  <FormField label="Desconto (%)">
                    <NumberInput min={0} max={100} value={discount} onChange={(val) => setDiscount(val)} precision={2}>
                      <NumberInputField placeholder="Opcional" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormField>
                </Stack>
                {previewPrice != null && (
                  <Flex
                    mt={4}
                    bg="white"
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor="gray.200"
                    px={4}
                    py={3}
                    align="center"
                    gap={2}
                  >
                    <Text variant="caption">Preço com desconto:</Text>
                    <Text fontSize="sm" fontWeight="bold" color="green.600">
                      R$ {formatCurrencyValue(previewPrice)}
                    </Text>
                    {discount && priceOriginal != null && (
                      <Badge colorScheme="green" ml={1}>
                        -{discount}%
                      </Badge>
                    )}
                  </Flex>
                )}
                <Text variant="caption" mt={3}>
                  Campos deixados em branco mantêm os valores atuais de cada curso.
                </Text>
              </Box>
            )}

            {mode === 'coupon' && (
              <Box bg="gray.50" borderRadius="md" p={4}>
                <Checkbox
                  isChecked={removeCoupon}
                  onChange={(e) => {
                    setRemoveCoupon(e.target.checked)
                    if (e.target.checked) setCouponValue('')
                  }}
                  mb={3}
                >
                  <Text fontSize="sm">Remover cupons</Text>
                </Checkbox>
                <FormField label="Cupom" isDisabled={removeCoupon}>
                  <Input
                    placeholder="Ex: PROMO2026"
                    value={couponValue}
                    onChange={(e) => setCouponValue(e.target.value.toUpperCase())}
                    isDisabled={removeCoupon}
                    maxLength={100}
                  />
                </FormField>
                <Text variant="caption" mt={3}>
                  {removeCoupon
                    ? 'O parâmetro cupom será removido da URL da Eduzz de todos os cursos selecionados.'
                    : 'O cupom será adicionado (ou substituído) no campo eduzz_url de todos os cursos selecionados.'}
                </Text>
              </Box>
            )}

            {/* CTA text (optional, available in all modes) */}
            <Box bg="gray.50" borderRadius="md" p={4} mt={5}>
              <FormField label="Texto CTA (opcional)">
                <Input
                  placeholder="Ex: Matricule-se agora!"
                  value={ctaText}
                  onChange={(e) => setCtaText(e.target.value)}
                  maxLength={255}
                />
              </FormField>
              <Text variant="caption" mt={2}>
                Se preenchido, o texto CTA será atualizado em todos os cursos selecionados. Se vazio, mantém o valor
                atual.
              </Text>
            </Box>
          </>
        )}
      </FormModal>

      <EditBulkScheduleModal
        schedule={editingSchedule}
        isOpen={isEditOpen}
        onClose={onEditClose}
        onSaved={() => loadSchedules(schedulesPage, schedulesFilter)}
      />
    </>
  )
}
